import React, { useEffect, useState, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Heart, MessageCircle, Send, Image as ImageIcon, Flag, Loader2, Sparkles, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { communityAPI, uploadAPI, userAPI, mediaUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TYPES = [
  { v: 'victoire', l: '🏆 Victoire' },
  { v: 'astuce', l: '💡 Astuce' },
  { v: 'question', l: '❓ Question' },
  { v: 'difficulte', l: '😅 Difficulté' },
  { v: 'texte', l: '💬 Message' },
];

const FILTERS = [
  { v: null, l: 'Tout' },
  { v: 'victoire', l: '🏆 Victoires' },
  { v: 'astuce', l: '💡 Astuces' },
  { v: 'question', l: '❓ Questions' },
  { v: 'difficulte', l: '😅 Difficultés' },
];

const QUESTIONS = [
  'Quelle est ta petite victoire du jour ?',
  "Qu'as-tu ressenti aujourd'hui ?",
  "Qu'est-ce qui a été facile ?",
  "Qu'est-ce qui a été difficile ?",
  'Quelle astuce veux-tu partager au groupe ?',
];

const typeLabel = (t) => TYPES.find((x) => x.v === t)?.l || '💬 Message';

const PostCard = ({ post, onChange }) => {
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likes, setLikes] = useState(post.likes_count);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [count, setCount] = useState(post.comments_count || 0);

  const toggleLike = async () => {
    const res = await communityAPI.like(post.id);
    setLiked(res.liked_by_me);
    setLikes(res.likes_count);
  };

  const loadComments = async () => {
    const c = await communityAPI.comments(post.id);
    setComments(c);
    setShowComments(true);
  };

  const addComment = async () => {
    if (!text.trim()) return;
    const c = await communityAPI.addComment(post.id, text.trim());
    setComments((s) => [...s, c]);
    setCount((n) => n + 1);
    setText('');
    toast.success('Encouragement envoyé ! +5 pts');
  };

  const report = async () => {
    await communityAPI.report(post.id);
    toast.success('Publication signalée au coach');
  };

  return (
    <Card className="p-4 rounded-2xl border-red-100 dark:border-red-900/50" data-testid={`post-${post.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <Avatar className="w-10 h-10">
          <AvatarImage src={mediaUrl(post.author?.avatar)} />
          <AvatarFallback className="bg-red-600 text-white text-sm">{post.author?.name?.charAt(0) || 'M'}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <p className="font-semibold text-sm text-red-950 dark:text-red-50">{post.author?.name}</p>
          <p className="text-xs text-muted-foreground">{post.challenge_day ? `Jour ${post.challenge_day}` : ''}</p>
        </div>
        <Badge variant="secondary" className="rounded-full text-xs">{typeLabel(post.type)}</Badge>
      </div>

      {post.content && <p className="text-sm text-foreground leading-relaxed mb-3 whitespace-pre-wrap">{post.content}</p>}
      {post.media_url && (
        <div className="rounded-xl overflow-hidden mb-3">
          {(post.media_url || '').match(/\.(mp4|mov|webm)/i) ? (
            <video src={mediaUrl(post.media_url)} controls className="w-full max-h-80 object-cover" />
          ) : (
            <img src={mediaUrl(post.media_url)} alt="publication" className="w-full h-56 object-cover bg-muted" />
          )}
        </div>
      )}

      <div className="flex items-center gap-4 text-sm">
        <button onClick={toggleLike} data-testid={`like-${post.id}`} className={`flex items-center gap-1.5 font-medium ${liked ? 'text-red-500' : 'text-muted-foreground'}`}>
          <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />{likes}
        </button>
        <button onClick={loadComments} data-testid={`comments-${post.id}`} className="flex items-center gap-1.5 font-medium text-muted-foreground">
          <MessageCircle className="w-4 h-4" />{count}
        </button>
        <button onClick={report} data-testid={`report-${post.id}`} className="ml-auto text-muted-foreground/60"><Flag className="w-3.5 h-3.5" /></button>
      </div>

      {showComments && (
        <div className="mt-3 pt-3 border-t border-border space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2">
              <Avatar className="w-7 h-7"><AvatarFallback className="bg-rose-600 text-white text-xs">{c.author?.name?.charAt(0) || 'M'}</AvatarFallback></Avatar>
              <div className="bg-muted rounded-2xl px-3 py-2 flex-1">
                <p className="text-xs font-semibold text-foreground">{c.author?.name}</p>
                <p className="text-sm text-foreground">{c.content}</p>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addComment()}
              data-testid={`comment-input-${post.id}`}
              placeholder="Un mot d'encouragement..."
              className="flex-1 rounded-full border border-input bg-background px-4 py-2 text-sm"
            />
            <Button size="icon" onClick={addComment} data-testid={`send-comment-${post.id}`} className="bg-orange-500 hover:bg-orange-600 rounded-full shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default function Community() {
  const { user, updateLocalUser } = useAuth();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('victoire');
  const [content, setContent] = useState('');
  const [media, setMedia] = useState(null);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileRef = useRef(null);
  const avatarRef = useRef(null);
  const question = QUESTIONS[new Date().getDate() % QUESTIONS.length];

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      const u = await userAPI.update({ avatar: res.url });
      updateLocalUser(u);
      toast.success('Photo de profil ajoutée !');
    } catch { toast.error("Échec de l'envoi"); }
    finally { setAvatarUploading(false); }
  };

  const load = (f = filter) => {
    setLoading(true);
    communityAPI.list(f ? { type: f } : {}).then(setPosts).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(null); }, []);

  const changeFilter = (f) => { setFilter(f); load(f); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      setMedia(res);
      if (type === 'texte') setType('victoire');
    } catch { toast.error("Échec de l'envoi"); }
    finally { setUploading(false); }
  };

  const publish = async () => {
    if (!content.trim() && !media) { toast.error('Ajoute un message ou une photo'); return; }
    setPosting(true);
    try {
      await communityAPI.create({ type, content: content.trim(), media_url: media?.url || null });
      setContent(''); setMedia(null);
      toast.success('Publié ! +5 pts');
      load(null); setFilter(null);
    } catch { toast.error('Échec de la publication'); }
    finally { setPosting(false); }
  };

  return (
    <div className="space-y-5 animate-rise">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-red-950 dark:text-red-50">Communauté</h1>
        <p className="text-muted-foreground text-sm mt-1">Partage, encourage, avance ensemble 💚</p>
      </div>

      {!user?.avatar && (
        <Card className="p-4 rounded-2xl border-red-100 dark:border-red-900/50 flex items-center gap-3 bg-red-50 dark:bg-red-900/20" data-testid="avatar-prompt">
          <div className="w-11 h-11 rounded-full bg-red-600 text-white flex items-center justify-center shrink-0">
            <Camera className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-950 dark:text-red-50">Ajoute ta photo de profil</p>
            <p className="text-xs text-muted-foreground">Elle apparaîtra avec ton nom sur tes publications.</p>
          </div>
          <Button size="sm" onClick={() => avatarRef.current?.click()} disabled={avatarUploading} data-testid="community-add-avatar" className="bg-orange-500 hover:bg-orange-600 rounded-full font-semibold shrink-0">
            {avatarUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
          </Button>
          <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="community-avatar-input" />
        </Card>
      )}

      {/* Daily question */}
      <Card className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-900/50 flex items-center gap-3">
        <Sparkles className="w-5 h-5 text-rose-600 shrink-0" />
        <p className="text-sm font-medium text-rose-900 dark:text-rose-200">{question}</p>
      </Card>

      {/* Composer */}
      <Card className="p-4 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="post-composer">
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-3">
          {TYPES.map((t) => (
            <button key={t.v} onClick={() => setType(t.v)} data-testid={`composer-type-${t.v}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${type === t.v ? 'bg-red-600 text-white' : 'bg-muted text-muted-foreground'}`}>
              {t.l}
            </button>
          ))}
        </div>
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} data-testid="composer-content"
          placeholder="Partage ta victoire, ton astuce ou ta question..." rows={2} className="resize-none rounded-xl" />
        {media && (
          <div className="mt-2 rounded-xl overflow-hidden relative">
            {media.media_type === 'video' ? <video src={mediaUrl(media.url)} className="w-full h-40 object-cover" /> : <img src={mediaUrl(media.url)} alt="" className="w-full h-40 object-cover" />}
          </div>
        )}
        <div className="flex items-center justify-between mt-3">
          <button onClick={() => fileRef.current?.click()} data-testid="composer-upload" className="flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-red-300">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Photo / vidéo
          </button>
          <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleFile} data-testid="composer-file-input" />
          <Button onClick={publish} disabled={posting} data-testid="publish-button" className="bg-orange-500 hover:bg-orange-600 rounded-full font-semibold">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publier'}
          </Button>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {FILTERS.map((f) => (
          <button key={f.l} onClick={() => changeFilter(f.v)} data-testid={`filter-${f.v || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${filter === f.v ? 'bg-rose-600 text-white' : 'bg-muted text-muted-foreground'}`}>
            {f.l}
          </button>
        ))}
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-red-600" /></div>
      ) : posts.length === 0 ? (
        <p className="text-center text-muted-foreground py-10 text-sm">Aucune publication pour le moment. Sois le premier à partager !</p>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => <PostCard key={p.id} post={p} onChange={() => load(filter)} />)}
        </div>
      )}
    </div>
  );
}
